/**
 * Iter XX.89 — Paid Instagram Personalisation Gate.
 *
 * Two-step, tap-only persona + goal picker shown ONCE to visitors
 * from paid Meta ads before they enter /chat. Under 5 seconds by
 * design — no typing, no email, no forms, no Continue button, one
 * tap advances immediately.
 *
 * Founder principle: personalisation, NOT qualification. The player
 * should never feel like they're applying for access.
 */
import { useEffect, useMemo, useState } from "react";
import { markGateCompleted } from "../lib/paidInstagramGate";
import { track } from "../lib/clarity";
import api from "../lib/api";

const PERSONA_OPTIONS = [
  { slug: "player", emoji: "🏏", label: "I'm a Cricketer" },
  { slug: "parent", emoji: "👨‍👩‍👦", label: "I'm a Parent" },
  { slug: "coach",  emoji: "🧢",  label: "I'm a Coach" },
];

const GOALS_BY_PERSONA = {
  player: {
    prompt: "What's your biggest goal right now?",
    options: [
      { slug: "become_better",         label: "Become a better cricketer" },
      { slug: "fix_something",         label: "Fix something in my game" },
      { slug: "perform_consistently",  label: "Perform more consistently" },
      { slug: "just_exploring",        label: "Just exploring" },
    ],
  },
  parent: {
    prompt: "What brings you here today?",
    options: [
      { slug: "help_child_improve",    label: "Help my child improve" },
      { slug: "understand_game",       label: "Understand their game better" },
      { slug: "looking_for_coaching",  label: "Looking for coaching support" },
      { slug: "just_exploring",        label: "Just exploring" },
    ],
  },
  coach: {
    prompt: "What brings you here today?",
    options: [
      { slug: "help_players_improve",     label: "Help my players improve" },
      { slug: "support_development",      label: "Support player development" },
      { slug: "curious_how_gully_works",  label: "Curious how Gully works" },
      { slug: "just_exploring",           label: "Just exploring" },
    ],
  },
};

/** Fire-and-forget analytics — best-effort, never blocks the UI. */
function _emit(event, payload, sessionId) {
  try { track(event, payload || {}); } catch { /* noop */ }
  try {
    if (sessionId) {
      api.post("/web/track", {
        session_id: sessionId,
        event,
        payload: payload || {},
      }).catch(() => {});
    }
  } catch { /* noop */ }
}

/**
 * Two-step gate. Renders as a full-screen overlay so it never has to
 * fight the underlying Chat for real estate.
 *
 * @param {object}   props
 * @param {string}   props.sessionId       Current chat session id — used for /web/track.
 * @param {function} props.onComplete      Called with `{ persona, goal }` once step 2 is tapped.
 */
export default function PaidInstagramPersonaliseGate({ sessionId, onComplete }) {
  const [persona, setPersona] = useState(null);
  const [advancing, setAdvancing] = useState(false);

  // Fire `gate_shown` once, when the component mounts.
  useEffect(() => {
    _emit("paid_ig_gate_shown", { step: "persona" }, sessionId);
  }, [sessionId]);

  const goalStep = useMemo(
    () => (persona ? GOALS_BY_PERSONA[persona] : null),
    [persona],
  );

  function _pickPersona(slug) {
    if (advancing) return;
    setPersona(slug);
    _emit("persona_selected", { persona: slug }, sessionId);
  }

  function _pickGoal(slug) {
    if (advancing || !persona) return;
    setAdvancing(true);
    _emit("goal_selected", { persona, goal: slug }, sessionId);
    _emit("paid_ig_gate_completed", { persona, goal: slug }, sessionId);
    markGateCompleted({ persona, goal: slug });
    // Small delay so the tap feels acknowledged before the chat mounts.
    setTimeout(() => {
      try { onComplete?.({ persona, goal: slug }); } catch { /* noop */ }
    }, 120);
  }

  return (
    <div
      data-testid="paid-ig-gate"
      className="fixed inset-0 z-50 bg-neutral-950 overflow-y-auto flex flex-col items-stretch"
    >
      <div className="w-full max-w-md mx-auto px-5 pt-14 pb-10 flex-1 flex flex-col">
        <div className="mb-8">
          <h1
            className="text-3xl sm:text-4xl font-semibold text-neutral-100 leading-tight"
            data-testid="paid-ig-gate-title"
          >
            {persona
              ? goalStep?.prompt
              : "Let's personalise your coach."}
          </h1>
          <p
            className="mt-2 text-neutral-400 text-base"
            data-testid="paid-ig-gate-subtitle"
          >
            {persona
              ? "One more tap and you're in."
              : "This takes 2 seconds and helps Gully coach you better."}
          </p>
        </div>

        {!persona && (
          <div className="space-y-3" data-testid="paid-ig-gate-persona-step">
            {PERSONA_OPTIONS.map((opt) => (
              <button
                key={opt.slug}
                type="button"
                onClick={() => _pickPersona(opt.slug)}
                data-testid={`paid-ig-gate-persona-${opt.slug}`}
                className="w-full flex items-center gap-4 rounded-2xl border border-neutral-800 bg-neutral-900/70 px-5 py-5 text-left text-neutral-100 hover:border-emerald-700 hover:bg-neutral-900 active:scale-[0.99] transition-all"
              >
                <span aria-hidden="true" className="text-3xl">{opt.emoji}</span>
                <span className="text-lg font-medium">{opt.label}</span>
              </button>
            ))}
          </div>
        )}

        {persona && goalStep && (
          <div className="space-y-3" data-testid="paid-ig-gate-goal-step">
            {goalStep.options.map((opt) => (
              <button
                key={opt.slug}
                type="button"
                disabled={advancing}
                onClick={() => _pickGoal(opt.slug)}
                data-testid={`paid-ig-gate-goal-${opt.slug}`}
                className="w-full flex items-center rounded-2xl border border-neutral-800 bg-neutral-900/70 px-5 py-5 text-left text-neutral-100 hover:border-emerald-700 hover:bg-neutral-900 active:scale-[0.99] disabled:opacity-60 transition-all"
              >
                <span className="text-lg font-medium">{opt.label}</span>
              </button>
            ))}
            <button
              type="button"
              disabled={advancing}
              onClick={() => setPersona(null)}
              data-testid="paid-ig-gate-back"
              className="mt-2 text-sm text-neutral-500 hover:text-neutral-300 underline underline-offset-4 disabled:opacity-40"
            >
              ← Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
